import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Container, Row, Col, Card, CardBody } from "reactstrap";
import { toast } from "react-toastify";
import useSocket from "hooks/useSocket";
import { getSocketEmit, getValuesSocketData } from "helpers/secretManager";
import { post } from "helpers/api_helper";
import { EXPORT_STATS } from "helpers/url_helper";
import {
  getBtncStatistics,
  getClaimStatistics,
  getEnergyStatistics,
  getTaskStatistics,
  getUserStatistics,
} from "store/statistics/actions";

import UsersTab from "./Tabs/UserTab";
import TokensTab from "./Tabs/TokensTab";
import ClaimsTab from "./Tabs/ClaimTab";
import TasksTab from "./Tabs/TaskTab";
import ChartsTab from "./Tabs/ChartsTab";
import EnergyTab from "./Tabs/EnergyTab";
import PermissionGate from "components/PermissionGate";

// ─── Tab config ───────────────────────────────────────────────────────────────
const TABS = [
  { id: "users", label: "Users", color: "#378ADD" },
  { id: "tokens", label: "BTNC", color: "#639922" },
  { id: "claims", label: "Claims", color: "#D85A30" },
  { id: "energy", label: "Energy", color: "#534AB7" },
  { id: "tasks", label: "Tasks", color: "#BA7517" },
  { id: "charts", label: "Charts", color: "#6c757d" },
];

const DEFAULT_DATE_RANGE = { startDate: "2026-03-17", endDate: "2026-03-19" };

const Statistics = () => {
  const dispatch = useDispatch();
  const { socket } = useSocket();

  const [activeTab, setActiveTab] = useState("users");
  const [exportLoading, setExportLoading] = useState(false);
  const [dateRange, setDateRange] = useState(DEFAULT_DATE_RANGE);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [tempDateRange, setTempDateRange] = useState(DEFAULT_DATE_RANGE);

  const fetchedTabs = useRef(new Set());

  const {
    userLoading,
    userStatistics,
    btncLoading,
    btncStatistics,
    energyLoading,
    energyStatistics,
    claimLoading,
    claimStatistics,
    taskLoading,
    taskStatistics,
  } = useSelector(({ Statistics }) => ({
    userLoading: Statistics?.userLoading,
    userStatistics: Statistics?.userStatistics ?? {},

    btncLoading: Statistics?.btncLoading,
    btncStatistics: Statistics?.btncStatistics ?? {},

    energyLoading: Statistics?.energyLoading,
    energyStatistics: Statistics?.energyStatistics ?? {},

    claimLoading: Statistics?.claimLoading,
    claimStatistics: Statistics?.claimStatistics ?? {},

    taskLoading: Statistics?.taskLoading,
    taskStatistics: Statistics?.taskStatistics ?? {},
  }));

  const tabFetchers = {
    users: () => {
      dispatch(
        getUserStatistics({
          data: dateRange,
          callback: (err) => {
            if (err) toast.error(err);
          },
        }),
      );
    },

    tokens: () => {
      dispatch(
        getBtncStatistics({
          data: dateRange,
          callback: (err) => {
            if (err) toast.error(err);
          },
        }),
      );
      dispatch(
        getEnergyStatistics({
          data: dateRange,
          callback: (err) => {
            if (err) toast.error(err);
          },
        }),
      );
    },

    claims: () => {
      dispatch(
        getClaimStatistics({
          data: dateRange,
          callback: (err) => {
            if (err) toast.error(err);
          },
        }),
      );
    },

    tasks: () => {
      dispatch(
        getTaskStatistics({
          data: dateRange,
          callback: (err) => {
            if (err) toast.error(err);
          },
        }),
      );
    },

    charts: () => { },
  };

  // Fetch data when active tab changes
  useEffect(() => {
    if (fetchedTabs.current.has(activeTab)) return;

    const fetcher = tabFetchers[activeTab];
    if (fetcher) {
      fetcher();
      fetchedTabs.current.add(activeTab);
    }
  }, [activeTab]);

  // Refetch data when date range changes
  useEffect(() => {
    fetchedTabs.current.clear(); // Clear cache to force refetch
    const fetcher = tabFetchers[activeTab];
    if (fetcher) {
      fetcher();
    }
  }, [dateRange]);

  const isTabLoading =
    {
      users: userLoading,
      tokens: btncLoading || energyLoading,
      claims: claimLoading,
      tasks: taskLoading,
      charts: false,
    }[activeTab] ?? false;

  const handleDateRangeChange = (e) => {
    const { name, value } = e.target;
    setTempDateRange((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const applyDateRange = () => {
    setDateRange(tempDateRange);
    setShowDatePicker(false);
  };

  const resetDateRange = () => {
    setTempDateRange(DEFAULT_DATE_RANGE);
    setDateRange(DEFAULT_DATE_RANGE);
    setShowDatePicker(false);
  };

  const exportData = async () => {
    try {
      setExportLoading(true);
      const { signature, nonce } = await getValuesSocketData();
      const res = await post(
        EXPORT_STATS,
        {
          key: "global",
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
        },
        { signature, nonce }
      );
      if (res?.status) {
        toast.success("File exported successfully!");
        if (res?.data) window.open(res.data, "_blank");
      } else {
        toast.error(res?.message);
      }
    } catch (err) {
      toast.error(err?.message);
    } finally {
      setExportLoading(false);
    }
  };

  const renderPanel = () => {
    switch (activeTab) {
      case "users":
        return <UsersTab data={userStatistics} />;
      case "tokens":
        return (
          <TokensTab btncData={btncStatistics} energyData={energyStatistics} />
        );
      case "energy":
        return <EnergyTab energyData={energyStatistics} />;
      case "claims":
        return <ClaimsTab data={claimStatistics} />;
      case "tasks":
        return <TasksTab data={taskStatistics} />;
      case "charts":
        return <ChartsTab />;
      default:
        return null;
    }
  };

  // ──────────────────────────────────────────────────────────────────────────
  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          {/* ── Page header ── */}
          <Row className="mb-3">
            <Col>
              <Card>
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <h4 style={{ margin: 0 }}>Statistics</h4>
                      <p
                        style={{
                          fontSize: 13,
                          color: "#6c757d",
                          margin: "4px 0 0",
                        }}
                      >
                        Dashboard overview across all modules
                      </p>
                    </div>
                    <PermissionGate permission="exports_statistics">
                      <button
                        onClick={exportData}
                        disabled={exportLoading}
                        className="btn btn-outline-secondary btn-sm"
                        style={{ minWidth: 90 }}
                      >
                        {exportLoading ? "Exporting…" : "Export"}
                      </button>
                    </PermissionGate>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          <Row>
            <Col>
              <Card>
                <CardBody>
                  {/* ── Tabs and Date Filter ── */}
                  <div
                    className="d-flex align-items-center justify-between flex-wrap"
                    style={{
                      gap: 4,
                      borderBottom: "1px solid #dee2e6",
                      marginBottom: "1.5rem",
                      position: "relative",
                    }}
                  >
                    {/* Tabs */}
                    <div className="d-flex align-items-center">
                      {TABS.map((tab) => (
                        <button
                          key={tab.id}
                          onClick={() => setActiveTab(tab.id)}
                          style={{
                            padding: "10px 18px",
                            fontSize: 13,
                            fontWeight: activeTab === tab.id ? 500 : 400,
                            border: "none",
                            background: "none",
                            cursor: "pointer",
                            color: activeTab === tab.id ? "#212529" : "#6c757d",
                            borderBottom: `2px solid ${activeTab === tab.id ? tab.color : "transparent"
                              }`,
                            marginBottom: -1,
                            whiteSpace: "nowrap",
                            transition: "color 0.15s",
                          }}
                        >
                          <span
                            style={{
                              display: "inline-block",
                              width: 7,
                              height: 7,
                              borderRadius: "50%",
                              background: tab.color,
                              marginRight: 7,
                              verticalAlign: "middle",
                            }}
                          />
                          {tab.label}
                        </button>
                      ))}
                    </div>

                    {/* Date Filter Button */}
                    <div style={{ marginLeft: "auto", position: "relative" }}>
                      <button
                        onClick={() => setShowDatePicker((prev) => !prev)}
                        className="btn btn-outline-secondary btn-sm"
                        style={{
                          whiteSpace: "nowrap",
                          fontSize: 12,
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                        }}
                      >
                        📅{" "}
                        {dateRange.startDate === DEFAULT_DATE_RANGE.startDate &&
                          dateRange.endDate === DEFAULT_DATE_RANGE.endDate
                          ? "Default Range"
                          : `${dateRange.startDate} → ${dateRange.endDate}`}
                      </button>

                      {/* Date Picker Dropdown */}
                      {showDatePicker && (
                        <div
                          style={{
                            position: "absolute",
                            top: "100%",
                            right: 0,
                            background: "white",
                            border: "1px solid #dee2e6",
                            borderRadius: "6px",
                            padding: "15px",
                            marginTop: "8px",
                            zIndex: 1000,
                            minWidth: "320px",
                            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                          }}
                        >
                          <div style={{ marginBottom: "12px" }}>
                            <label
                              style={{
                                display: "block",
                                fontSize: 12,
                                fontWeight: 600,
                                marginBottom: "6px",
                                color: "#495057",
                              }}
                            >
                              Start Date
                            </label>
                            <input
                              type="date"
                              name="startDate"
                              value={tempDateRange.startDate}
                              onChange={handleDateRangeChange}
                              style={{
                                width: "100%",
                                padding: "8px 10px",
                                border: "1px solid #ced4da",
                                borderRadius: "4px",
                                fontSize: 12,
                                boxSizing: "border-box",
                              }}
                            />
                          </div>

                          <div style={{ marginBottom: "12px" }}>
                            <label
                              style={{
                                display: "block",
                                fontSize: 12,
                                fontWeight: 600,
                                marginBottom: "6px",
                                color: "#495057",
                              }}
                            >
                              End Date
                            </label>
                            <input
                              type="date"
                              name="endDate"
                              max={new Date().toLocaleDateString("en-CA")}
                              value={tempDateRange.endDate}
                              onChange={handleDateRangeChange}
                              style={{
                                width: "100%",
                                padding: "8px 10px",
                                border: "1px solid #ced4da",
                                borderRadius: "4px",
                                fontSize: 12,
                                boxSizing: "border-box",
                              }}
                            />
                          </div>
                          <button
                            onClick={() => {
                              const today = new Date()
                                .toISOString()
                                .split("T")[0];

                              const newRange = {
                                startDate: today,
                                endDate: today,
                              };

                              setTempDateRange(newRange);
                              setDateRange(newRange);
                              setShowDatePicker(false);
                            }}
                            className="btn btn-outline-primary btn-sm"
                            style={{
                              width: "100%",
                              fontSize: 12,
                              marginBottom: "10px",
                            }}
                          >
                            Today
                          </button>
                          <div
                            style={{
                              display: "flex",
                              gap: "8px",
                              marginTop: "12px",
                            }}
                          >
                            <button
                              onClick={applyDateRange}
                              className="btn btn-primary btn-sm"
                              style={{ flex: 1, fontSize: 12 }}
                            >
                              Apply
                            </button>
                            <button
                              onClick={resetDateRange}
                              className="btn btn-outline-secondary btn-sm"
                              style={{ flex: 1, fontSize: 12 }}
                            >
                              Reset
                            </button>
                            <button
                              onClick={() => setShowDatePicker(false)}
                              className="btn btn-light btn-sm"
                              style={{ flex: 1, fontSize: 12 }}
                            >
                              Close
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {isTabLoading ? (
                    <div className="text-center py-4">
                      <div
                        className="spinner-border spinner-border-sm text-secondary"
                        role="status"
                      />
                      <p
                        style={{ fontSize: 13, color: "#6c757d", marginTop: 8 }}
                      >
                        Loading data…
                      </p>
                    </div>
                  ) : (
                    renderPanel()
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default Statistics;
